import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { authFetch } from '@/lib/authFetch';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────
// Icon helper
// ─────────────────────────────────────────────

function Icon({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('material-symbols-outlined', className)}>{name}</span>
  );
}

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface InvoiceLine {
  id: string;
  concepto: string;
  cantidad: number;
  precioUnitario: number;
  descuento: number;
  tipoIva: number;
  recargoEquivalencia: number;
  retencion: number;
  esSuplido: boolean;
}

interface Invoice {
  id: string;
  numero: string;
  serie: string;
  fecha: string;
  fechaVencimiento: string;
  fechaOperacion: string;
  clienteNombre: string;
  clienteNif: string;
  clienteDireccion: string;
  clienteEmail: string;
  lineas: InvoiceLine[];
  baseImponible: number;
  totalIva: number;
  totalRecargoEquivalencia: number;
  totalRetenciones: number;
  totalSuplidos: number;
  totalFactura: number;
  estado: 'borrador' | 'emitida' | 'enviada' | 'pagada' | 'anulada' | 'rectificada';
  verifactu: 'enviada' | 'pendiente' | 'rechazada' | 'no_aplica';
  notas: string;
  empresaId: string;
  templateId?: string;
}

interface InvoiceSeries {
  id: string;
  codigo: string;
  nombre: string;
  tipo: 'ordinaria' | 'rectificativa' | 'simplificada';
  prefijo: string;
  siguienteNumero: number;
  verifactu: boolean;
  activa: boolean;
}

interface InvoiceTemplate {
  id: string;
  nombre: string;
  logoUrl?: string;
  colorPrimario: string;
  colorSecundario: string;
  piePagina: string;
  datosBancarios: string;
  condicionesPago: string;
  activa: boolean;
}

interface Empresa {
  id: string;
  razon_social: string;
  nif: string;
}

type TabKey = 'facturas' | 'nueva' | 'series' | 'templates';

type FilterType = 'estado' | 'cliente' | 'fecha' | 'importe';

type DocumentType = 'facturas' | 'simplificadas' | 'presupuestos' | 'albaranes' | 'proformas';

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatNumber = (value: number, decimals = 2): string => {
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const todayISO = (): string => new Date().toISOString().split('T')[0];

const generateId = (): string => Math.random().toString(36).substring(2, 11);

const createEmptyLine = (): InvoiceLine => ({
  id: generateId(),
  concepto: '',
  cantidad: 1,
  precioUnitario: 0,
  descuento: 0,
  tipoIva: 21,
  recargoEquivalencia: 0,
  retencion: 0,
  esSuplido: false,
});

const IVA_OPTIONS = [
  { value: 21, label: '21%' },
  { value: 10, label: '10%' },
  { value: 4, label: '4%' },
  { value: 0, label: '0%' },
  { value: -1, label: 'Exento' },
];

const RETENCION_OPTIONS = [
  { value: 0, label: 'N/A' },
  { value: 15, label: '15%' },
  { value: 7, label: '7%' },
];

const DOCUMENT_TYPES: { key: DocumentType; label: string }[] = [
  { key: 'facturas', label: 'Facturas' },
  { key: 'simplificadas', label: 'F. Simplificadas' },
  { key: 'presupuestos', label: 'Presupuestos' },
  { key: 'albaranes', label: 'Albaranes' },
  { key: 'proformas', label: 'Proformas' },
];

const FILTER_TYPE_LABELS: Record<FilterType, string> = {
  estado: 'Estado',
  cliente: 'Cliente',
  fecha: 'Fecha',
  importe: 'Importe',
};

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────

function EstadoBadge({ estado }: { estado: Invoice['estado'] }) {
  const config: Record<Invoice['estado'], { className: string; label: string; dot: string }> = {
    borrador: { className: 'bg-slate-50 text-calm-muted border-calm-border', label: 'Borrador', dot: 'bg-slate-400' },
    emitida: { className: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Emitida', dot: 'bg-blue-500' },
    enviada: { className: 'bg-cyan-50 text-cyan-700 border-cyan-200', label: 'Enviada', dot: 'bg-cyan-500' },
    pagada: { className: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Pagada', dot: 'bg-emerald-500' },
    anulada: { className: 'bg-red-50 text-red-600 border-red-200', label: 'Cancelada', dot: 'bg-red-500' },
    rectificada: { className: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Rectificada', dot: 'bg-amber-500' },
  };
  const c = config[estado] || config.borrador;
  return (
    <Badge variant="outline" className={cn('gap-1.5 rounded-lg font-light', c.className)}>
      <span className={cn('w-1.5 h-1.5 rounded-full', c.dot)} />
      {c.label}
    </Badge>
  );
}

function VeriFactuIcon({ status }: { status: Invoice['verifactu'] }) {
  if (status === 'enviada') {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-600" title="VeriFactu: Enviada">
        <Icon name="check_circle" className="text-[15px]" />
      </span>
    );
  }
  if (status === 'pendiente') {
    return (
      <span className="inline-flex items-center gap-1 text-amber-500" title="VeriFactu: Pendiente">
        <Icon name="schedule" className="text-[15px]" />
      </span>
    );
  }
  if (status === 'rechazada') {
    return (
      <span className="inline-flex items-center gap-1 text-red-500" title="VeriFactu: Rechazada">
        <Icon name="cancel" className="text-[15px]" />
      </span>
    );
  }
  return <span className="text-calm-accent">—</span>;
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

export default function AsesorFacturacion() {
  const { user, loading } = useAuth();
  const router = useRouter();

  // ── Tab state
  const [activeTab, setActiveTab] = useState<TabKey>('facturas');

  // ── Global data
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<string>('');
  const [series, setSeries] = useState<InvoiceSeries[]>([]);
  const [templates, setTemplates] = useState<InvoiceTemplate[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  // ── Facturas list state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [filterEmpresa, setFilterEmpresa] = useState<string>('');
  const [filterEstado, setFilterEstado] = useState<string>('');
  const [filterTipo, setFilterTipo] = useState<string>('');
  const [filterFechaDesde, setFilterFechaDesde] = useState<string>('');
  const [filterFechaHasta, setFilterFechaHasta] = useState<string>('');
  const [filterSearch, setFilterSearch] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<string>('fecha');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // ── Nueva Factura form state
  const [formSerieId, setFormSerieId] = useState<string>('');
  const [formNumeroPreview, setFormNumeroPreview] = useState<string>('');
  const [formClienteNif, setFormClienteNif] = useState('');
  const [formClienteNombre, setFormClienteNombre] = useState('');
  const [formClienteDireccion, setFormClienteDireccion] = useState('');
  const [formClienteEmail, setFormClienteEmail] = useState('');
  const [formFechaEmision, setFormFechaEmision] = useState(todayISO());
  const [formFechaVencimiento, setFormFechaVencimiento] = useState('');
  const [formFechaOperacion, setFormFechaOperacion] = useState('');
  const [formLineas, setFormLineas] = useState<InvoiceLine[]>([createEmptyLine()]);
  const [formNotas, setFormNotas] = useState('');
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null);

  // ── Series modal
  const [showSeriesModal, setShowSeriesModal] = useState(false);
  const [editingSeries, setEditingSeries] = useState<InvoiceSeries | null>(null);
  const [seriesForm, setSeriesForm] = useState({
    codigo: '',
    nombre: '',
    tipo: 'ordinaria' as InvoiceSeries['tipo'],
    prefijo: '',
    siguienteNumero: 1,
    verifactu: true,
  });

  // ── Template modal
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<InvoiceTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    nombre: '',
    colorPrimario: '#1e3a5f',
    colorSecundario: '#2563eb',
    piePagina: '',
    datosBancarios: '',
    condicionesPago: '',
  });

  // ── Invoice detail/preview
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // ── Document type filter
  const [documentType, setDocumentType] = useState<DocumentType>('facturas');

  // ── Dynamic filters
  const [visibleFilters, setVisibleFilters] = useState<FilterType[]>([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterImporteMin, setFilterImporteMin] = useState('');
  const [filterImporteMax, setFilterImporteMax] = useState('');

  // ── Emisor data
  const [formEmisorNombre, setFormEmisorNombre] = useState('');
  const [formEmisorCif, setFormEmisorCif] = useState('');
  const [formEmisorDireccion, setFormEmisorDireccion] = useState('');
  const [formEmisorCiudad, setFormEmisorCiudad] = useState('');
  const [formEmisorCp, setFormEmisorCp] = useState('');
  const [formClienteCiudad, setFormClienteCiudad] = useState('');
  const [formClienteCp, setFormClienteCp] = useState('');

  // ── Editor toggles
  const [editandoEmisor, setEditandoEmisor] = useState(false);
  const [editandoReceptor, setEditandoReceptor] = useState(false);

  // ── Invoice preview dialog
  const [mostrarPreview, setMostrarPreview] = useState(false);

  // ── CSV file input ref
  const csvFileInputRef = useRef<HTMLInputElement>(null);

  // ─────────────────────────────────────────────
  // Auth guard
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'asesor')) {
      router.push('/');
    }
  }, [user, loading, router]);

  // ─────────────────────────────────────────────
  // Data fetching
  // ─────────────────────────────────────────────

  const loadEmpresas = useCallback(async () => {
    try {
      const { data: assigned, error } = await supabase
        .from('asesor_cliente')
        .select('cliente_id, clientes(id, razon_social, nombre_comercial, nif)')
        .eq('asesor_id', user?.id);

      if (error) throw error;

      const empresasList: Empresa[] = (assigned || [])
        .map((ac: any) => ac.clientes)
        .filter(Boolean)
        .map((c: any) => ({
          id: c.id,
          razon_social: c.razon_social || '',
          nif: c.nif || '',
        }));

      setEmpresas(empresasList);

      // Auto-select first empresa if none selected
      if (empresasList.length > 0 && !selectedEmpresaId) {
        setSelectedEmpresaId(empresasList[0].id);
        return empresasList[0].id;
      }
      return selectedEmpresaId || (empresasList.length > 0 ? empresasList[0].id : '');
    } catch (error: any) {
      console.error('Error loading empresas:', error);
      return '';
    }
  }, [user?.id, selectedEmpresaId]);

  const loadData = useCallback(async (empresaId?: string) => {
    const empId = empresaId || selectedEmpresaId;
    if (!user?.id || !empId) return;
    setLoadingData(true);
    try {
      const [invoicesRes, seriesRes, templatesRes] = await Promise.allSettled([
        authFetch(`/api/invoicing/invoices?empresa_id=${empId}`).then(r => r.ok ? r.json() : Promise.reject(r)),
        authFetch(`/api/invoicing/series?empresa_id=${empId}`).then(r => r.ok ? r.json() : Promise.reject(r)),
        authFetch(`/api/invoicing/templates?empresa_id=${empId}`).then(r => r.ok ? r.json() : Promise.reject(r)),
      ]);

      if (invoicesRes.status === 'fulfilled' && invoicesRes.value?.data) {
        setInvoices(invoicesRes.value.data);
      }
      if (seriesRes.status === 'fulfilled' && seriesRes.value?.data) {
        setSeries(seriesRes.value.data);
        if (seriesRes.value.data.length > 0 && !formSerieId) {
          setFormSerieId(seriesRes.value.data[0].id);
        }
      }
      if (templatesRes.status === 'fulfilled' && templatesRes.value?.data) {
        setTemplates(templatesRes.value.data);
      }
    } catch (err) {
      console.error('Error loading invoicing data:', err);
    } finally {
      setLoadingData(false);
    }
  }, [user, formSerieId, selectedEmpresaId]);

  useEffect(() => {
    if (user && user.rol === 'asesor') {
      loadEmpresas().then((empId) => {
        if (empId) loadData(empId);
      });
    }
  }, [user, loadEmpresas, loadData]);

  // ── Number preview when serie changes
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (formSerieId) {
      const serie = series.find(s => s.id === formSerieId);
      if (serie) {
        const num = String(serie.siguienteNumero).padStart(4, '0');
        setFormNumeroPreview(`${serie.prefijo}${num}`);
      }
    }
  }, [formSerieId, series]);

  // ─────────────────────────────────────────────
  // Line calculations (useMemo for performance)
  // ─────────────────────────────────────────────

  const lineCalculations = useMemo(() => {
    return formLineas.map(line => {
      const subtotal = line.cantidad * line.precioUnitario;
      const descuentoAmount = subtotal * (line.descuento / 100);
      const baseLinea = subtotal - descuentoAmount;

      if (line.esSuplido) {
        return {
          lineId: line.id,
          subtotal,
          descuentoAmount,
          baseLinea,
          ivaAmount: 0,
          recargoAmount: 0,
          retencionAmount: 0,
          totalLinea: baseLinea,
        };
      }

      const ivaRate = line.tipoIva === -1 ? 0 : line.tipoIva;
      const ivaAmount = baseLinea * (ivaRate / 100);
      const recargoAmount = baseLinea * (line.recargoEquivalencia / 100);
      const retencionAmount = baseLinea * (line.retencion / 100);
      const totalLinea = baseLinea + ivaAmount + recargoAmount - retencionAmount;

      return {
        lineId: line.id,
        subtotal,
        descuentoAmount,
        baseLinea,
        ivaAmount,
        recargoAmount,
        retencionAmount,
        totalLinea,
      };
    });
  }, [formLineas]);

  const invoiceTotals = useMemo(() => {
    let baseImponible = 0;
    let totalSuplidos = 0;
    let totalRetenciones = 0;
    let totalRecargoEquivalencia = 0;
    const ivaBreakdown: Record<number, { base: number; cuota: number }> = {};

    formLineas.forEach((line, i) => {
      const calc = lineCalculations[i];
      if (!calc) return;

      if (line.esSuplido) {
        totalSuplidos += calc.baseLinea;
        return;
      }

      baseImponible += calc.baseLinea;
      totalRetenciones += calc.retencionAmount;
      totalRecargoEquivalencia += calc.recargoAmount;

      const ivaRate = line.tipoIva === -1 ? 0 : line.tipoIva;
      if (!ivaBreakdown[ivaRate]) {
        ivaBreakdown[ivaRate] = { base: 0, cuota: 0 };
      }
      ivaBreakdown[ivaRate].base += calc.baseLinea;
      ivaBreakdown[ivaRate].cuota += calc.ivaAmount;
    });

    const totalIva = Object.values(ivaBreakdown).reduce((sum, v) => sum + v.cuota, 0);
    const totalFactura = baseImponible + totalIva + totalRecargoEquivalencia - totalRetenciones + totalSuplidos;

    return {
      baseImponible,
      totalIva,
      totalRecargoEquivalencia,
      totalRetenciones,
      totalSuplidos,
      totalFactura,
      ivaBreakdown,
    };
  }, [formLineas, lineCalculations]);

  // ─────────────────────────────────────────────
  // Filtered + sorted invoices
  // ─────────────────────────────────────────────

  const filteredInvoices = useMemo(() => {
    let result = [...invoices];

    if (series.length > 0) {
      switch (documentType) {
        case 'facturas': {
          const serieIds = series.filter(s => s.tipo === 'ordinaria' || s.tipo === 'rectificativa').map(s => s.id);
          if (serieIds.length > 0) result = result.filter(inv => serieIds.includes(inv.serie));
          break;
        }
        case 'simplificadas': {
          const serieIds = series.filter(s => s.tipo === 'simplificada').map(s => s.id);
          result = result.filter(inv => serieIds.includes(inv.serie));
          break;
        }
        case 'presupuestos':
        case 'albaranes':
        case 'proformas':
          result = [];
          break;
      }
    }

    if (filterEmpresa) result = result.filter(inv => inv.empresaId === filterEmpresa);
    if (filterEstado) result = result.filter(inv => inv.estado === filterEstado);
    if (filterTipo) result = result.filter(inv => inv.serie === filterTipo);
    if (filterFechaDesde) result = result.filter(inv => inv.fecha >= filterFechaDesde);
    if (filterFechaHasta) result = result.filter(inv => inv.fecha <= filterFechaHasta);
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      result = result.filter(inv =>
        inv.clienteNif.toLowerCase().includes(q) ||
        inv.clienteNombre.toLowerCase().includes(q) ||
        inv.numero.toLowerCase().includes(q)
      );
    }
    if (filterImporteMin) {
      const min = parseFloat(filterImporteMin);
      if (!isNaN(min)) result = result.filter(inv => inv.totalFactura >= min);
    }
    if (filterImporteMax) {
      const max = parseFloat(filterImporteMax);
      if (!isNaN(max)) result = result.filter(inv => inv.totalFactura <= max);
    }

    result.sort((a, b) => {
      let valA: string | number = '';
      let valB: string | number = '';
      switch (sortColumn) {
        case 'numero': valA = a.numero; valB = b.numero; break;
        case 'fecha': valA = a.fecha; valB = b.fecha; break;
        case 'cliente': valA = a.clienteNombre; valB = b.clienteNombre; break;
        case 'total': valA = a.totalFactura; valB = b.totalFactura; break;
        case 'estado': valA = a.estado; valB = b.estado; break;
        default: valA = a.fecha; valB = b.fecha;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [invoices, filterEmpresa, filterEstado, filterTipo, filterFechaDesde, filterFechaHasta, filterSearch, filterImporteMin, filterImporteMax, sortColumn, sortDirection, documentType, series]);

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / pageSize));
  const paginatedInvoices = filteredInvoices.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // ─────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const toggleInvoiceSelection = (id: string) => {
    setSelectedInvoices(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllSelection = () => {
    if (selectedInvoices.size === paginatedInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(paginatedInvoices.map(inv => inv.id)));
    }
  };

  const addLine = () => setFormLineas(prev => [...prev, createEmptyLine()]);

  const removeLine = (lineId: string) => {
    if (formLineas.length <= 1) { toast.error('La factura debe tener al menos una línea'); return; }
    setFormLineas(prev => prev.filter(l => l.id !== lineId));
  };

  const updateLine = (lineId: string, field: keyof InvoiceLine, value: string | number | boolean) => {
    setFormLineas(prev => prev.map(l => l.id !== lineId ? l : { ...l, [field]: value }));
  };

  const clearAllLines = () => {
    if (formLineas.length <= 1) return;
    if (!confirm('¿Eliminar todas las líneas de la factura?')) return;
    setFormLineas([createEmptyLine()]);
    toast.success('Todas las líneas eliminadas');
  };

  const handleExcelUpload = () => {
    csvFileInputRef.current?.click();
  };

  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text !== 'string') { toast.error('No se pudo leer el archivo'); return; }

      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) { toast.error('El archivo CSV debe tener al menos una cabecera y una fila de datos'); return; }

      // Detect separator (semicolon common in ES locale, fallback to comma)
      const separator = lines[0].includes(';') ? ';' : ',';
      const headers = lines[0].split(separator).map(h => h.trim().toLowerCase().replace(/["']/g, ''));

      // Map header names to InvoiceLine fields
      const fieldMap: Record<string, keyof InvoiceLine> = {
        descripcion: 'concepto', concepto: 'concepto', servicio: 'concepto', producto: 'concepto',
        cantidad: 'cantidad', cant: 'cantidad', qty: 'cantidad', unidades: 'cantidad',
        precio: 'precioUnitario', precio_unitario: 'precioUnitario', preciounitario: 'precioUnitario', importe_unitario: 'precioUnitario', precio_unit: 'precioUnitario',
        iva: 'tipoIva', tipo_iva: 'tipoIva', tipoiva: 'tipoIva', '%iva': 'tipoIva',
        descuento: 'descuento', dto: 'descuento',
      };

      const colMapping: (keyof InvoiceLine | null)[] = headers.map(h => fieldMap[h] ?? null);

      if (!colMapping.some(c => c === 'concepto')) {
        toast.error('CSV debe tener una columna "descripcion" o "concepto"');
        return;
      }

      const parsedLines: InvoiceLine[] = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(separator).map(c => c.trim().replace(/["']/g, ''));
        const newLine = createEmptyLine();
        colMapping.forEach((field, idx) => {
          if (!field || idx >= cols.length) return;
          const val = cols[idx];
          if (field === 'concepto') newLine.concepto = val;
          else if (field === 'cantidad') newLine.cantidad = parseFloat(val.replace(',', '.')) || 1;
          else if (field === 'precioUnitario') newLine.precioUnitario = parseFloat(val.replace(',', '.')) || 0;
          else if (field === 'tipoIva') {
            const ivaVal = parseFloat(val.replace(',', '.').replace('%', ''));
            newLine.tipoIva = [21, 10, 4, 0, -1].includes(ivaVal) ? ivaVal : 21;
          }
          else if (field === 'descuento') newLine.descuento = parseFloat(val.replace(',', '.')) || 0;
        });
        if (newLine.concepto) parsedLines.push(newLine);
      }

      if (parsedLines.length === 0) { toast.error('No se encontraron líneas válidas en el CSV'); return; }

      setFormLineas(parsedLines);
      toast.success(`${parsedLines.length} línea${parsedLines.length > 1 ? 's' : ''} importada${parsedLines.length > 1 ? 's' : ''}`);
    };
    reader.onerror = () => toast.error('Error al leer el archivo');
    reader.readAsText(file);

    // Reset input so same file can be re-uploaded
    e.target.value = '';
  };

  const addFilter = (type: FilterType) => {
    if (!visibleFilters.includes(type)) setVisibleFilters(prev => [...prev, type]);
    setShowFilterDropdown(false);
  };

  const removeFilter = (type: FilterType) => {
    setVisibleFilters(prev => prev.filter(f => f !== type));
    switch (type) {
      case 'estado': setFilterEstado(''); break;
      case 'cliente': setFilterSearch(''); break;
      case 'fecha': setFilterFechaDesde(''); setFilterFechaHasta(''); break;
      case 'importe': setFilterImporteMin(''); setFilterImporteMax(''); break;
    }
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setVisibleFilters([]);
    setFilterSearch('');
    setFilterEstado('');
    setFilterTipo('');
    setFilterFechaDesde('');
    setFilterFechaHasta('');
    setFilterImporteMin('');
    setFilterImporteMax('');
    setCurrentPage(1);
  };

  const hasActiveFilters = !!(filterSearch || filterEstado || filterTipo || filterFechaDesde || filterFechaHasta || filterImporteMin || filterImporteMax);

  const handleNifLookup = async () => {
    if (!formClienteNif || formClienteNif.length < 8) return;
    try {
      const res = await authFetch(`/api/invoicing/invoices?lookup_nif=${encodeURIComponent(formClienteNif)}`);
      if (res.ok) {
        const data = await res.json();
        if (data?.cliente) {
          setFormClienteNombre(data.cliente.nombre || '');
          setFormClienteDireccion(data.cliente.direccion || '');
          setFormClienteEmail(data.cliente.email || '');
          toast.success('Cliente encontrado');
        }
      }
    } catch {
      // Silent - user can fill manually
    }
  };

  const resetForm = () => {
    setEditingInvoiceId(null);
    setFormClienteNif('');
    setFormClienteNombre('');
    setFormClienteDireccion('');
    setFormClienteEmail('');
    setFormFechaEmision(todayISO());
    setFormFechaVencimiento('');
    setFormFechaOperacion('');
    setFormLineas([createEmptyLine()]);
    setFormNotas('');
    if (series.length > 0) setFormSerieId(series[0].id);
  };

  const resetFormFull = resetForm;

  const handleSaveInvoice = async (emitir: boolean) => {
    if (!formSerieId) { toast.error('Selecciona una serie'); return; }
    if (!formClienteNif) { toast.error('El NIF del cliente es obligatorio'); return; }
    if (!formClienteNombre) { toast.error('El nombre del cliente es obligatorio'); return; }
    if (formLineas.some(l => !l.concepto.trim())) { toast.error('Todas las líneas deben tener un concepto'); return; }
    if (formLineas.some(l => l.cantidad <= 0)) { toast.error('La cantidad debe ser mayor que 0'); return; }

    setSavingInvoice(true);
    try {
      const payload = {
        id: editingInvoiceId,
        serieId: formSerieId,
        clienteNif: formClienteNif,
        clienteNombre: formClienteNombre,
        clienteDireccion: formClienteDireccion,
        clienteEmail: formClienteEmail,
        fechaEmision: formFechaEmision,
        fechaVencimiento: formFechaVencimiento,
        fechaOperacion: formFechaOperacion,
        lineas: formLineas,
        notas: formNotas,
        emitir,
        totals: invoiceTotals,
      };

      const endpoint = emitir ? '/api/invoicing/emit' : '/api/invoicing/invoices';
      const method = editingInvoiceId ? 'PUT' : 'POST';

      const res = await authFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al guardar la factura');
      }

      toast.success(emitir ? 'Factura emitida correctamente' : 'Borrador guardado');
      resetForm();
      setActiveTab('facturas');
      await loadData();
    } catch (err: any) {
      toast.error(err.message || 'Error al guardar');
    } finally {
      setSavingInvoice(false);
    }
  };

  const handleDuplicate = async (invoiceId: string) => {
    try {
      const res = await authFetch('/api/invoicing/duplicate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ invoiceId }) });
      if (!res.ok) throw new Error('Error al duplicar');
      toast.success('Factura duplicada como borrador');
      await loadData();
    } catch (err: any) { toast.error(err.message); }
  };

  const handleRectificativa = async (invoiceId: string) => {
    try {
      const res = await authFetch('/api/invoicing/rectificativa', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ invoiceId }) });
      if (!res.ok) throw new Error('Error al crear rectificativa');
      toast.success('Factura rectificativa creada');
      await loadData();
    } catch (err: any) { toast.error(err.message); }
  };

  const handleSendEmail = async (invoiceId: string) => {
    try {
      const res = await authFetch('/api/invoicing/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ invoiceId }) });
      if (!res.ok) throw new Error('Error al enviar');
      toast.success('Factura enviada por email');
      await loadData();
    } catch (err: any) { toast.error(err.message); }
  };

  const handleDownloadXml = async (invoiceId: string) => {
    try {
      const res = await authFetch(`/api/invoicing/invoices?id=${invoiceId}&format=xml`);
      if (!res.ok) throw new Error('Error al descargar');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura-${invoiceId}.xml`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('XML descargado');
    } catch (err: any) { toast.error(err.message); }
  };

  const handleEditInvoice = (invoice: Invoice) => {
    if (invoice.estado !== 'borrador') { toast.error('Solo se pueden editar borradores'); return; }
    setEditingInvoiceId(invoice.id);
    setFormSerieId(invoice.serie);
    setFormClienteNif(invoice.clienteNif);
    setFormClienteNombre(invoice.clienteNombre);
    setFormClienteDireccion(invoice.clienteDireccion);
    setFormClienteEmail(invoice.clienteEmail);
    setFormFechaEmision(invoice.fecha);
    setFormFechaVencimiento(invoice.fechaVencimiento);
    setFormFechaOperacion(invoice.fechaOperacion);
    setFormLineas(invoice.lineas.length > 0 ? invoice.lineas : [createEmptyLine()]);
    setFormNotas(invoice.notas);
    setActiveTab('nueva');
  };

  const handlePreview = (invoice: Invoice) => {
    setPreviewInvoice(invoice);
    setShowPreview(true);
  };

  const handleBulkEmit = async () => {
    if (selectedInvoices.size === 0) { toast.error('Selecciona facturas para emitir'); return; }
    const borradores = Array.from(selectedInvoices).filter(id => invoices.find(i => i.id === id)?.estado === 'borrador');
    if (borradores.length === 0) { toast.error('No hay borradores seleccionados'); return; }
    try {
      const res = await authFetch('/api/invoicing/emit', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ invoiceIds: borradores }) });
      if (!res.ok) throw new Error('Error al emitir');
      toast.success(`${borradores.length} factura(s) emitida(s)`);
      setSelectedInvoices(new Set());
      await loadData();
    } catch (err: any) { toast.error(err.message); }
  };

  const handleBulkSendEmail = async () => {
    if (selectedInvoices.size === 0) { toast.error('Selecciona facturas para enviar'); return; }
    try {
      const res = await authFetch('/api/invoicing/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ invoiceIds: Array.from(selectedInvoices) }) });
      if (!res.ok) throw new Error('Error al enviar');
      toast.success(`${selectedInvoices.size} factura(s) enviada(s) por email`);
      setSelectedInvoices(new Set());
      await loadData();
    } catch (err: any) { toast.error(err.message); }
  };

  // ── Series CRUD
  const handleSaveSeries = async () => {
    if (!seriesForm.codigo || !seriesForm.nombre) { toast.error('Código y nombre son obligatorios'); return; }
    try {
      const method = editingSeries ? 'PUT' : 'POST';
      const payload = editingSeries ? { ...seriesForm, id: editingSeries.id } : seriesForm;
      const res = await authFetch('/api/invoicing/series', { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('Error al guardar serie');
      toast.success(editingSeries ? 'Serie actualizada' : 'Serie creada');
      setShowSeriesModal(false);
      setEditingSeries(null);
      await loadData();
    } catch (err: any) { toast.error(err.message); }
  };

  const handleDeleteSeries = async (serieId: string) => {
    if (!confirm('¿Eliminar esta serie? Las facturas existentes no se verán afectadas.')) return;
    try {
      const res = await authFetch('/api/invoicing/series', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: serieId }) });
      if (!res.ok) throw new Error('Error al eliminar');
      toast.success('Serie eliminada');
      await loadData();
    } catch (err: any) { toast.error(err.message); }
  };

  const openEditSeries = (serie: InvoiceSeries) => {
    setEditingSeries(serie);
    setSeriesForm({ codigo: serie.codigo, nombre: serie.nombre, tipo: serie.tipo, prefijo: serie.prefijo, siguienteNumero: serie.siguienteNumero, verifactu: serie.verifactu });
    setShowSeriesModal(true);
  };

  const openNewSeries = () => {
    setEditingSeries(null);
    setSeriesForm({ codigo: '', nombre: '', tipo: 'ordinaria', prefijo: '', siguienteNumero: 1, verifactu: true });
    setShowSeriesModal(true);
  };

  // ── Template CRUD
  const handleSaveTemplate = async () => {
    if (!templateForm.nombre) { toast.error('El nombre es obligatorio'); return; }
    try {
      const method = editingTemplate ? 'PUT' : 'POST';
      const payload = editingTemplate ? { ...templateForm, id: editingTemplate.id } : templateForm;
      const res = await authFetch('/api/invoicing/templates', { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('Error al guardar plantilla');
      toast.success(editingTemplate ? 'Plantilla actualizada' : 'Plantilla creada');
      setShowTemplateModal(false);
      setEditingTemplate(null);
      await loadData();
    } catch (err: any) { toast.error(err.message); }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('¿Eliminar esta plantilla?')) return;
    try {
      const res = await authFetch('/api/invoicing/templates', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: templateId }) });
      if (!res.ok) throw new Error('Error al eliminar');
      toast.success('Plantilla eliminada');
      await loadData();
    } catch (err: any) { toast.error(err.message); }
  };

  const openEditTemplate = (template: InvoiceTemplate) => {
    setEditingTemplate(template);
    setTemplateForm({ nombre: template.nombre, colorPrimario: template.colorPrimario, colorSecundario: template.colorSecundario, piePagina: template.piePagina, datosBancarios: template.datosBancarios, condicionesPago: template.condicionesPago });
    setShowTemplateModal(true);
  };

  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ nombre: '', colorPrimario: '#1e3a5f', colorSecundario: '#2563eb', piePagina: '', datosBancarios: '', condicionesPago: '' });
    setShowTemplateModal(true);
  };

  // ─────────────────────────────────────────────
  // Loading / guard
  // ─────────────────────────────────────────────

  if (loading || !user) {
    return (
      <Layout rol="asesor">
        <div className="min-h-screen flex items-center justify-center bg-calm-bg">
          <div className="text-center space-y-4">
            <Skeleton className="h-24 w-24 rounded-full mx-auto" />
            <Skeleton className="h-4 w-40 mx-auto" />
          </div>
        </div>
      </Layout>
    );
  }

  // ─────────────────────────────────────────────
  // Stats
  // ─────────────────────────────────────────────

  const stats = {
    total: invoices.length,
    borradores: invoices.filter(i => i.estado === 'borrador').length,
    emitidas: invoices.filter(i => i.estado === 'emitida' || i.estado === 'enviada').length,
    pagadas: invoices.filter(i => i.estado === 'pagada').length,
    facturado: invoices.filter(i => i.estado !== 'anulada' && i.estado !== 'borrador').reduce((sum, i) => sum + i.totalFactura, 0),
  };

  // ─────────────────────────────────────────────
  // Tab config
  // ─────────────────────────────────────────────

  const tabs = [
    { key: 'facturas' as TabKey, icon: 'description', label: 'Facturas' },
    { key: 'nueva' as TabKey, icon: 'add', label: editingInvoiceId ? 'Editar Factura' : 'Nueva Factura' },
    { key: 'series' as TabKey, icon: 'settings', label: 'Series' },
    { key: 'templates' as TabKey, icon: 'palette', label: 'Plantillas' },
  ];

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────

  return (
    <Layout rol="asesor">
      <Head>
        <title>Facturación | Asesor</title>
      </Head>

      <div className="h-[calc(100vh-72px)] overflow-y-auto bg-calm-bg">
        <div className="px-12 pb-12">

          {/* ─── Header ─── */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pt-12 mb-8">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-2">Asesor</p>
              <h1 className="page-title">Facturación</h1>
              <p className="text-sm text-calm-muted mt-2 font-light">
                Gestión de facturas, series y plantillas
              </p>
            </div>
            <Button
              onClick={() => { resetForm(); setActiveTab('nueva'); }}
              className="rounded-huge font-light gap-2"
            >
              <Icon name="add" className="text-[18px]" />
              Nueva Factura
            </Button>
          </div>

          {/* ─── Tabs ─── */}
          <div className="border-b border-calm-border flex items-center gap-0 mb-10">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => {
                  if (tab.key === 'nueva' && activeTab !== 'nueva') resetForm();
                  setActiveTab(tab.key);
                }}
                className={cn(
                  'flex items-center gap-2 px-5 py-3 text-sm border-b-2 transition-all duration-200 -mb-px',
                  activeTab === tab.key
                    ? 'border-calm-text text-calm-text font-medium'
                    : 'border-transparent text-calm-muted font-light hover:text-calm-text'
                )}
              >
                <Icon name={tab.icon} className="text-[18px]" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* ─── Content area ─── */}
          <div className="space-y-8">

            {/* ═══════════════════════════════════════
                TAB 1 — FACTURAS LIST
               ═══════════════════════════════════════ */}
            {activeTab === 'facturas' && (
              <>
                {/* Stats row */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
                  {[
                    { label: 'Total', value: stats.total },
                    { label: 'Borradores', value: stats.borradores },
                    { label: 'Emitidas', value: stats.emitidas },
                    { label: 'Pagadas', value: stats.pagadas },
                  ].map(s => (
                    <Card key={s.label} className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none hover:shadow-sm transition-shadow">
                      <CardContent className="p-6">
                        <p className="stat-value">{s.value}</p>
                        <p className="stat-label">{s.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                  <Card className="col-span-2 lg:col-span-1 bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none hover:shadow-sm transition-shadow">
                    <CardContent className="p-6">
                      <p className="text-xl font-light text-calm-text font-display tabular-nums">{formatCurrency(stats.facturado)}</p>
                      <p className="stat-label">Facturado</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Document type selector pills */}
                <div className="flex items-center gap-1 p-1 bg-steel-azure-50 rounded-huge w-fit">
                  {DOCUMENT_TYPES.map(dt => (
                    <button
                      key={dt.key}
                      onClick={() => { setDocumentType(dt.key); setCurrentPage(1); }}
                      className={cn(
                        'px-4 py-2 text-sm rounded-2xl transition-all duration-200 font-light',
                        documentType === dt.key
                          ? 'bg-white shadow-sm text-calm-text font-medium'
                          : 'text-calm-muted hover:text-calm-text'
                      )}
                    >
                      {dt.label}
                    </button>
                  ))}
                </div>

                {/* Dynamic filter bar */}
                <Card className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge shadow-none">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex-1 min-w-[220px] max-w-md">
                        <div className="relative">
                          <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-calm-accent text-[18px]" />
                          <Input
                            type="text"
                            value={filterSearch}
                            onChange={e => { setFilterSearch(e.target.value); setCurrentPage(1); }}
                            placeholder="NIF, nombre o nº factura…"
                            className="pl-10 border-calm-border rounded-2xl text-sm font-light"
                          />
                        </div>
                      </div>

                      <DropdownMenu open={showFilterDropdown} onOpenChange={setShowFilterDropdown}>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="gap-1.5 rounded-huge border-calm-border text-calm-muted font-light hover:text-calm-text">
                            <Icon name="filter_list" className="text-[16px]" />
                            Añadir filtro
                            <Icon name="expand_more" className={cn('text-[16px] transition-transform', showFilterDropdown && 'rotate-180')} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-44 rounded-2xl border-calm-border">
                          {(['estado', 'cliente', 'fecha', 'importe'] as FilterType[]).filter(t => !visibleFilters.includes(t)).map(type => (
                            <DropdownMenuItem key={type} onClick={() => addFilter(type)} className="text-sm font-light">
                              {FILTER_TYPE_LABELS[type]}
                            </DropdownMenuItem>
                          ))}
                          {visibleFilters.length >= 4 && (
                            <p className="px-3 py-2 text-xs text-calm-accent">Todos los filtros activos</p>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      {hasActiveFilters && (
                        <button onClick={clearAllFilters} className="text-sm font-light text-calm-muted hover:text-calm-text transition-colors">
                          Borrar filtros
                        </button>
                      )}
                    </div>

                    {/* Active filter rows */}
                    {visibleFilters.length > 0 && (
                      <div className="flex flex-wrap items-end gap-3 pt-4 border-t border-calm-border">
                        {visibleFilters.map(type => (
                          <div key={type} className="flex items-end gap-1.5">
                            {type === 'estado' && (
                              <div className="w-40">
                                <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-1.5 block">Estado</Label>
                                <Select value={filterEstado || 'all'} onValueChange={v => { setFilterEstado(v === 'all' ? '' : v); setCurrentPage(1); }}>
                                  <SelectTrigger className="h-9 rounded-2xl border-calm-border text-sm font-light"><SelectValue /></SelectTrigger>
                                  <SelectContent className="rounded-2xl border-calm-border">
                                    <SelectItem value="all">Todos</SelectItem>
                                    <SelectItem value="borrador">Borrador</SelectItem>
                                    <SelectItem value="emitida">Emitida</SelectItem>
                                    <SelectItem value="enviada">Enviada</SelectItem>
                                    <SelectItem value="pagada">Pagada</SelectItem>
                                    <SelectItem value="anulada">Cancelada</SelectItem>
                                    <SelectItem value="rectificada">Rectificada</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            {type === 'cliente' && (
                              <div className="w-48">
                                <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-1.5 block">Cliente</Label>
                                <Input
                                  type="text"
                                  value={filterSearch}
                                  onChange={e => { setFilterSearch(e.target.value); setCurrentPage(1); }}
                                  placeholder="Nombre o NIF…"
                                  className="h-9 rounded-2xl border-calm-border text-sm font-light"
                                />
                              </div>
                            )}
                            {type === 'fecha' && (
                              <div className="flex items-end gap-2">
                                <div className="w-36">
                                  <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-1.5 block">Desde</Label>
                                  <Input type="date" value={filterFechaDesde} onChange={e => { setFilterFechaDesde(e.target.value); setCurrentPage(1); }} className="h-9 rounded-2xl border-calm-border text-sm font-light" />
                                </div>
                                <div className="w-36">
                                  <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-1.5 block">Hasta</Label>
                                  <Input type="date" value={filterFechaHasta} onChange={e => { setFilterFechaHasta(e.target.value); setCurrentPage(1); }} className="h-9 rounded-2xl border-calm-border text-sm font-light" />
                                </div>
                              </div>
                            )}
                            {type === 'importe' && (
                              <div className="flex items-end gap-2">
                                <div className="w-32">
                                  <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-1.5 block">Importe mín.</Label>
                                  <Input type="number" value={filterImporteMin} onChange={e => { setFilterImporteMin(e.target.value); setCurrentPage(1); }} placeholder="0,00" min="0" step="0.01" className="h-9 tabular-nums rounded-2xl border-calm-border text-sm font-light" />
                                </div>
                                <div className="w-32">
                                  <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-1.5 block">Importe máx.</Label>
                                  <Input type="number" value={filterImporteMax} onChange={e => { setFilterImporteMax(e.target.value); setCurrentPage(1); }} placeholder="999.999" min="0" step="0.01" className="h-9 tabular-nums rounded-2xl border-calm-border text-sm font-light" />
                                </div>
                              </div>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => removeFilter(type)} className="h-9 w-9 text-calm-accent hover:text-red-500 rounded-2xl" title="Quitar filtro">
                              <Icon name="close" className="text-[16px]" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Bulk actions */}
                {selectedInvoices.size > 0 && (
                  <Card className="border-calm-border bg-slate-50 rounded-huge shadow-none">
                    <CardContent className="flex items-center gap-3 p-4">
                      <span className="text-sm font-light text-calm-text">
                        {selectedInvoices.size} seleccionada{selectedInvoices.size > 1 ? 's' : ''}
                      </span>
                      <Separator orientation="vertical" className="h-4 bg-calm-border" />
                      <Button variant="outline" size="sm" onClick={handleBulkEmit} className="gap-1.5 rounded-huge border-calm-border font-light text-calm-text">
                        <Icon name="check" className="text-[16px]" /> Emitir seleccionadas
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleBulkSendEmail} className="gap-1.5 rounded-huge border-calm-border font-light text-calm-text">
                        <Icon name="send" className="text-[16px]" /> Enviar por email
                      </Button>
                      <button onClick={() => setSelectedInvoices(new Set())} className="ml-auto text-sm font-light text-calm-muted hover:text-calm-text transition-colors">
                        Deseleccionar
                      </button>
                    </CardContent>
                  </Card>
                )}

                {/* Table */}
                <div className="bg-white rounded-huge border border-calm-border overflow-hidden">
                  {loadingData ? (
                    <div className="flex items-center justify-center py-16">
                      <Skeleton className="h-6 w-6 rounded-full" />
                    </div>
                  ) : filteredInvoices.length === 0 ? (
                    <div className="text-center py-16">
                      <Icon name="description" className="text-[40px] text-calm-accent mx-auto block mb-3" />
                      <p className="text-calm-text font-light">No se encontraron facturas</p>
                      <p className="text-sm text-calm-muted font-light mt-1">
                        {invoices.length === 0 ? 'Crea tu primera factura para empezar' : 'Prueba a cambiar los filtros'}
                      </p>
                      {invoices.length === 0 && (
                        <Button onClick={() => { resetForm(); setActiveTab('nueva'); }} className="mt-4 gap-2 rounded-huge font-light">
                          <Icon name="add" className="text-[18px]" /> Crear factura
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50/60 hover:bg-slate-50/60 border-b border-calm-border">
                            <TableHead className="w-10">
                              <Checkbox
                                checked={selectedInvoices.size === paginatedInvoices.length && paginatedInvoices.length > 0}
                                onCheckedChange={toggleAllSelection}
                              />
                            </TableHead>
                            {[
                              { key: 'numero', label: 'Nº Factura' },
                              { key: 'fecha', label: 'Fecha' },
                              { key: 'cliente', label: 'Cliente' },
                              { key: 'nif', label: 'NIF' },
                              { key: 'total', label: 'Total' },
                              { key: 'estado', label: 'Estado' },
                            ].map(col => (
                              <TableHead
                                key={col.key}
                                onClick={() => handleSort(col.key)}
                                className="text-[11px] font-medium text-calm-muted uppercase tracking-widest cursor-pointer hover:text-calm-text select-none transition-colors"
                              >
                                <span className="inline-flex items-center gap-1">
                                  {col.label}
                                  <Icon name="swap_vert" className={cn('text-[14px]', sortColumn === col.key ? 'text-calm-text' : 'text-calm-border')} />
                                </span>
                              </TableHead>
                            ))}
                            <TableHead className="text-center text-[11px] font-medium text-calm-muted uppercase tracking-widest">VeriFactu</TableHead>
                            <TableHead className="text-right text-[11px] font-medium text-calm-muted uppercase tracking-widest">Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedInvoices.map(inv => (
                            <TableRow
                              key={inv.id}
                              className={cn('group hover:bg-slate-50/50 transition-colors', selectedInvoices.has(inv.id) && 'bg-slate-50/50')}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={selectedInvoices.has(inv.id)}
                                  onCheckedChange={() => toggleInvoiceSelection(inv.id)}
                                />
                              </TableCell>
                              <TableCell className="font-mono text-xs font-medium text-calm-text">{inv.numero}</TableCell>
                              <TableCell className="text-calm-muted tabular-nums text-sm font-light">{formatDate(inv.fecha)}</TableCell>
                              <TableCell className="font-light text-calm-text max-w-[200px] truncate">{inv.clienteNombre}</TableCell>
                              <TableCell className="text-calm-muted font-mono text-xs">{inv.clienteNif}</TableCell>
                              <TableCell className="font-medium text-calm-text tabular-nums text-right text-sm">{formatCurrency(inv.totalFactura)}</TableCell>
                              <TableCell><EstadoBadge estado={inv.estado} /></TableCell>
                              <TableCell className="text-center"><VeriFactuIcon status={inv.verifactu} /></TableCell>
                              <TableCell className="text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 focus:opacity-100 rounded-2xl text-calm-muted">
                                      <Icon name="more_horiz" className="text-[18px]" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-52 rounded-2xl border-calm-border">
                                    <DropdownMenuItem onClick={() => handlePreview(inv)} className="gap-2.5 text-sm font-light">
                                      <Icon name="visibility" className="text-[18px] text-calm-accent" /> Ver factura
                                    </DropdownMenuItem>
                                    {inv.estado === 'borrador' && (
                                      <DropdownMenuItem onClick={() => handleEditInvoice(inv)} className="gap-2.5 text-sm font-light">
                                        <Icon name="edit" className="text-[18px] text-calm-accent" /> Editar
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => handleDuplicate(inv.id)} className="gap-2.5 text-sm font-light">
                                      <Icon name="content_copy" className="text-[18px] text-calm-accent" /> Duplicar
                                    </DropdownMenuItem>
                                    {inv.estado !== 'anulada' && inv.estado !== 'borrador' && (
                                      <DropdownMenuItem onClick={() => handleRectificativa(inv.id)} className="gap-2.5 text-sm font-light">
                                        <Icon name="undo" className="text-[18px] text-calm-accent" /> Rectificativa
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator className="bg-calm-border" />
                                    <DropdownMenuItem onClick={() => handleSendEmail(inv.id)} className="gap-2.5 text-sm font-light">
                                      <Icon name="mail" className="text-[18px] text-calm-accent" /> Enviar email
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDownloadXml(inv.id)} className="gap-2.5 text-sm font-light">
                                      <Icon name="download" className="text-[18px] text-calm-accent" /> Descargar XML
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between px-6 py-4 border-t border-calm-border">
                          <span className="text-xs text-calm-muted font-light">
                            {filteredInvoices.length} factura{filteredInvoices.length !== 1 ? 's' : ''} · Página {currentPage} de {totalPages}
                          </span>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="h-8 w-8 rounded-2xl text-calm-muted">
                              <Icon name="chevron_left" className="text-[18px]" />
                            </Button>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNum: number;
                              if (totalPages <= 5) pageNum = i + 1;
                              else if (currentPage <= 3) pageNum = i + 1;
                              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                              else pageNum = currentPage - 2 + i;
                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => setCurrentPage(pageNum)}
                                  className={cn(
                                    'h-8 w-8 rounded-2xl text-xs transition-all duration-200',
                                    currentPage === pageNum
                                      ? 'bg-calm-text text-white font-medium'
                                      : 'text-calm-muted font-light hover:bg-slate-50 hover:text-calm-text'
                                  )}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}
                            <Button variant="ghost" size="icon" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="h-8 w-8 rounded-2xl text-calm-muted">
                              <Icon name="chevron_right" className="text-[18px]" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            {/* ═══════════════════════════════════════
                TAB 2 — NUEVA FACTURA
               ═══════════════════════════════════════ */}
            {activeTab === 'nueva' && (
              <div className="space-y-8 max-w-[1100px]">
                {/* Header: Serie + Number */}
                <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                  <CardContent className="p-8">
                    <div className="flex flex-wrap items-end gap-6">
                      <div className="flex-1 min-w-[200px]">
                        <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Serie</Label>
                        <Select value={formSerieId || undefined} onValueChange={setFormSerieId}>
                          <SelectTrigger className="rounded-2xl border-calm-border text-sm font-light"><SelectValue placeholder="Seleccionar serie…" /></SelectTrigger>
                          <SelectContent className="rounded-2xl border-calm-border">
                            {series.map(s => (
                              <SelectItem key={s.id} value={s.id} className="text-sm font-light">{s.codigo} — {s.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-3 px-5 py-3 bg-steel-azure-50 rounded-2xl border border-steel-azure-200">
                        <Icon name="tag" className="text-[18px] text-calm-accent" />
                        <div>
                          <span className="text-[10px] text-calm-muted uppercase tracking-[0.15em] block leading-none">Nº Factura</span>
                          <span className="text-lg font-light font-mono text-calm-text font-display tracking-wide">{formNumeroPreview || '—'}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Emisor + Receptor */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                    <CardContent className="p-8">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-sm font-medium text-calm-text flex items-center gap-2">
                          <Icon name="business" className="text-[18px] text-calm-accent" /> Emisor
                        </h3>
                        <button onClick={() => setEditandoEmisor(prev => !prev)} className="text-xs text-calm-muted hover:text-calm-text font-light transition-colors flex items-center gap-1">
                          <Icon name={editandoEmisor ? 'check_circle' : 'edit'} className="text-[14px]" />
                          {editandoEmisor ? 'Guardar' : 'Editar Emisor'}
                        </button>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Razón Social</Label>
                          <Input type="text" value={formEmisorNombre} onChange={e => setFormEmisorNombre(e.target.value)} placeholder="Tu empresa S.L." className="bg-slate-50 border-calm-border rounded-2xl text-sm font-light" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">CIF</Label>
                          <Input type="text" value={formEmisorCif} onChange={e => setFormEmisorCif(e.target.value.toUpperCase())} placeholder="B00000000" className="bg-slate-50 border-calm-border rounded-2xl text-sm font-light font-mono" />
                        </div>
                        {editandoEmisor && (
                          <>
                            <div>
                              <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Dirección</Label>
                              <Input type="text" value={formEmisorDireccion} onChange={e => setFormEmisorDireccion(e.target.value)} placeholder="Calle, nº, piso" className="bg-slate-50 border-calm-border rounded-2xl text-sm font-light" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Ciudad</Label>
                                <Input type="text" value={formEmisorCiudad} onChange={e => setFormEmisorCiudad(e.target.value)} placeholder="Madrid" className="bg-slate-50 border-calm-border rounded-2xl text-sm font-light" />
                              </div>
                              <div>
                                <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Código Postal</Label>
                                <Input type="text" value={formEmisorCp} onChange={e => setFormEmisorCp(e.target.value)} placeholder="28001" className="bg-slate-50 border-calm-border rounded-2xl text-sm font-light font-mono" />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                    <CardContent className="p-8">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-sm font-medium text-calm-text flex items-center gap-2">
                          <Icon name="credit_card" className="text-[18px] text-calm-accent" /> Receptor
                        </h3>
                        <button onClick={() => setEditandoReceptor(prev => !prev)} className="text-xs text-calm-muted hover:text-calm-text font-light transition-colors flex items-center gap-1">
                          <Icon name={editandoReceptor ? 'check_circle' : 'edit'} className="text-[14px]" />
                          {editandoReceptor ? 'Guardar' : 'Editar Receptor'}
                        </button>
                      </div>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">NIF / CIF *</Label>
                            <div className="flex gap-2">
                              <Input type="text" value={formClienteNif} onChange={e => setFormClienteNif(e.target.value.toUpperCase())} onBlur={handleNifLookup} placeholder="B12345678" className="font-mono border-calm-border rounded-2xl text-sm font-light" />
                              <Button variant="outline" size="icon" onClick={handleNifLookup} title="Buscar cliente" className="h-10 w-10 rounded-2xl border-calm-border text-calm-muted">
                                <Icon name="search" className="text-[18px]" />
                              </Button>
                            </div>
                          </div>
                          <div>
                            <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Razón Social *</Label>
                            <Input type="text" value={formClienteNombre} onChange={e => setFormClienteNombre(e.target.value)} placeholder="Nombre o razón social" className="border-calm-border rounded-2xl text-sm font-light" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Dirección</Label>
                            <Input type="text" value={formClienteDireccion} onChange={e => setFormClienteDireccion(e.target.value)} placeholder="Calle, nº, CP, Ciudad" className="border-calm-border rounded-2xl text-sm font-light" />
                          </div>
                          <div>
                            <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Email</Label>
                            <Input type="email" value={formClienteEmail} onChange={e => setFormClienteEmail(e.target.value)} placeholder="cliente@empresa.com" className="border-calm-border rounded-2xl text-sm font-light" />
                          </div>
                        </div>
                        {editandoReceptor && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-dashed border-calm-border">
                            <div className="sm:col-span-2">
                              <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Dirección Completa</Label>
                              <Input type="text" value={formClienteDireccion} onChange={e => setFormClienteDireccion(e.target.value)} placeholder="Calle, número, piso, puerta" className="border-calm-border rounded-2xl text-sm font-light" />
                            </div>
                            <div>
                              <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Ciudad</Label>
                              <Input type="text" value={formClienteCiudad} onChange={e => setFormClienteCiudad(e.target.value)} placeholder="Madrid" className="border-calm-border rounded-2xl text-sm font-light" />
                            </div>
                            <div>
                              <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Código Postal</Label>
                              <Input type="text" value={formClienteCp} onChange={e => setFormClienteCp(e.target.value)} placeholder="28001" className="border-calm-border rounded-2xl text-sm font-light font-mono" />
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Dates */}
                <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                  <CardContent className="p-8">
                    <h3 className="text-sm font-medium text-calm-text mb-6">Fechas</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                      <div>
                        <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Fecha Emisión *</Label>
                        <Input type="date" value={formFechaEmision} onChange={e => setFormFechaEmision(e.target.value)} className="border-calm-border rounded-2xl text-sm font-light" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Fecha Vencimiento</Label>
                        <Input type="date" value={formFechaVencimiento} onChange={e => setFormFechaVencimiento(e.target.value)} className="border-calm-border rounded-2xl text-sm font-light" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Fecha Operación</Label>
                        <Input type="date" value={formFechaOperacion} onChange={e => setFormFechaOperacion(e.target.value)} className="border-calm-border rounded-2xl text-sm font-light" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Lines table */}
                <div className="bg-white rounded-huge border border-calm-border overflow-hidden">
                  <div className="flex items-center justify-between px-8 py-4 border-b border-calm-border bg-slate-50/40">
                    <h3 className="text-sm font-medium text-calm-text flex items-center gap-2">
                      <Icon name="receipt_long" className="text-[18px] text-calm-accent" /> Líneas de factura
                    </h3>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={handleExcelUpload} className="gap-1.5 rounded-huge border-calm-border font-light text-calm-muted">
                        <Icon name="upload" className="text-[16px]" /> Subir Excel
                      </Button>
                      {formLineas.length > 1 && (
                        <Button variant="outline" size="sm" onClick={clearAllLines} className="gap-1.5 rounded-huge border-red-200 text-red-600 font-light hover:bg-red-50">
                          <Icon name="delete" className="text-[16px]" /> Borrar todas
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={addLine} className="gap-1.5 rounded-huge border-calm-border font-light text-calm-text">
                        <Icon name="add_circle" className="text-[16px]" /> Añadir línea
                      </Button>
                    </div>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/30 hover:bg-slate-50/30 border-b border-calm-border">
                        <TableHead className="text-[11px] font-medium text-calm-muted uppercase tracking-widest min-w-[220px]">Concepto</TableHead>
                        <TableHead className="text-center text-[11px] font-medium text-calm-muted uppercase tracking-widest w-20">Cant.</TableHead>
                        <TableHead className="text-right text-[11px] font-medium text-calm-muted uppercase tracking-widest w-28">Precio</TableHead>
                        <TableHead className="text-center text-[11px] font-medium text-calm-muted uppercase tracking-widest w-20">Dto%</TableHead>
                        <TableHead className="text-center text-[11px] font-medium text-calm-muted uppercase tracking-widest w-24">IVA</TableHead>
                        <TableHead className="text-center text-[11px] font-medium text-calm-muted uppercase tracking-widest w-20">R.E.%</TableHead>
                        <TableHead className="text-center text-[11px] font-medium text-calm-muted uppercase tracking-widest w-24">Ret.</TableHead>
                        <TableHead className="text-center text-[11px] font-medium text-calm-muted uppercase tracking-widest w-16">Supl.</TableHead>
                        <TableHead className="text-right text-[11px] font-medium text-calm-muted uppercase tracking-widest w-28">Total</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formLineas.map((line, idx) => {
                        const calc = lineCalculations[idx];
                        return (
                          <TableRow key={line.id} className="group hover:bg-slate-50/50 transition-colors">
                            <TableCell><Input type="text" value={line.concepto} onChange={e => updateLine(line.id, 'concepto', e.target.value)} placeholder="Descripción del servicio o producto" className="h-8 text-sm font-light border-calm-border rounded-xl" /></TableCell>
                            <TableCell><Input type="number" value={line.cantidad} onChange={e => updateLine(line.id, 'cantidad', parseFloat(e.target.value) || 0)} min="0" step="any" className="h-8 text-sm text-center tabular-nums font-light border-calm-border rounded-xl" /></TableCell>
                            <TableCell><Input type="number" value={line.precioUnitario} onChange={e => updateLine(line.id, 'precioUnitario', parseFloat(e.target.value) || 0)} min="0" step="0.01" className="h-8 text-sm text-right tabular-nums font-light border-calm-border rounded-xl" /></TableCell>
                            <TableCell><Input type="number" value={line.descuento} onChange={e => updateLine(line.id, 'descuento', parseFloat(e.target.value) || 0)} min="0" max="100" step="0.01" className="h-8 text-sm text-center tabular-nums font-light border-calm-border rounded-xl" /></TableCell>
                            <TableCell>
                              <Select value={String(line.tipoIva)} onValueChange={v => updateLine(line.id, 'tipoIva', parseInt(v))}>
                                <SelectTrigger className="h-8 text-sm font-light border-calm-border rounded-xl"><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-xl border-calm-border">
                                  {IVA_OPTIONS.map(opt => (<SelectItem key={opt.value} value={String(opt.value)} className="text-sm font-light">{opt.label}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell><Input type="number" value={line.recargoEquivalencia} onChange={e => updateLine(line.id, 'recargoEquivalencia', parseFloat(e.target.value) || 0)} min="0" step="0.01" className="h-8 text-sm text-center tabular-nums font-light border-calm-border rounded-xl" /></TableCell>
                            <TableCell>
                              <Select value={String(line.retencion)} onValueChange={v => updateLine(line.id, 'retencion', parseInt(v))}>
                                <SelectTrigger className="h-8 text-sm font-light border-calm-border rounded-xl"><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-xl border-calm-border">
                                  {RETENCION_OPTIONS.map(opt => (<SelectItem key={opt.value} value={String(opt.value)} className="text-sm font-light">{opt.label}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox checked={line.esSuplido} onCheckedChange={checked => updateLine(line.id, 'esSuplido', !!checked)} />
                            </TableCell>
                            <TableCell className="text-right font-medium text-calm-text tabular-nums whitespace-nowrap text-sm">{formatCurrency(calc?.totalLinea || 0)}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="icon" onClick={() => removeLine(line.id)} className="h-7 w-7 text-calm-accent hover:text-red-500 opacity-0 group-hover:opacity-100 rounded-xl" title="Eliminar línea">
                                <Icon name="remove_circle" className="text-[16px]" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  <div className="px-8 py-3 border-t border-calm-border">
                    <button onClick={addLine} className="flex items-center gap-1.5 text-sm font-light text-calm-muted hover:text-calm-text transition-colors">
                      <Icon name="add_circle" className="text-[18px]" /> Añadir línea
                    </button>
                  </div>
                </div>

                {/* Totals summary + Notes */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                  <Card className="lg:col-span-3 bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                    <CardContent className="p-8">
                      <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-3 block">Observaciones / Notas</Label>
                      <Textarea value={formNotas} onChange={e => setFormNotas(e.target.value)} rows={5} placeholder="Notas adicionales para la factura…" className="resize-none border-calm-border rounded-2xl text-sm font-light" />
                    </CardContent>
                  </Card>

                  <Card className="lg:col-span-2 bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                    <CardContent className="p-8">
                      <h4 className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-4">Resumen</h4>
                      <div className="space-y-2.5">
                        <div className="flex justify-between text-sm"><span className="text-calm-muted font-light">Total sin impuestos</span><span className="font-light text-calm-text tabular-nums">{formatCurrency(invoiceTotals.baseImponible)}</span></div>
                        {Object.entries(invoiceTotals.ivaBreakdown).map(([rate, data]) => (
                          <div key={rate} className="flex justify-between text-sm">
                            <span className="text-calm-muted font-light">IVA {rate}% <span className="text-xs text-calm-accent">s/ {formatCurrency(data.base)}</span></span>
                            <span className="text-calm-text tabular-nums font-light">{formatCurrency(data.cuota)}</span>
                          </div>
                        ))}
                        {invoiceTotals.totalRecargoEquivalencia > 0 && (
                          <div className="flex justify-between text-sm"><span className="text-calm-muted font-light">Recargo Equivalencia</span><span className="text-calm-text tabular-nums font-light">{formatCurrency(invoiceTotals.totalRecargoEquivalencia)}</span></div>
                        )}
                        {invoiceTotals.totalRetenciones > 0 && (
                          <div className="flex justify-between text-sm"><span className="text-calm-muted font-light">Retenciones</span><span className="text-red-600 tabular-nums font-light">-{formatCurrency(invoiceTotals.totalRetenciones)}</span></div>
                        )}
                        {invoiceTotals.totalSuplidos > 0 && (
                          <div className="flex justify-between text-sm"><span className="text-calm-muted font-light">Suplidos</span><span className="text-calm-text tabular-nums font-light">{formatCurrency(invoiceTotals.totalSuplidos)}</span></div>
                        )}
                        <Separator className="my-2 bg-calm-border" />
                        <div className="flex justify-between text-sm"><span className="text-calm-text font-medium">Total factura</span><span className="font-medium text-calm-text tabular-nums">{formatCurrency(invoiceTotals.totalFactura)}</span></div>
                        <div className="pt-4 mt-3 border-t-2 border-calm-text">
                          <div className="flex justify-between items-baseline">
                            <span className="text-[10px] font-medium text-calm-text uppercase tracking-widest">Total a pagar</span>
                            <span className="text-2xl font-light text-calm-text tabular-nums font-display">{formatCurrency(invoiceTotals.totalFactura)}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Action buttons */}
                <div className="flex flex-wrap items-center gap-3 pb-8">
                  <Button variant="outline" onClick={() => handleSaveInvoice(false)} disabled={savingInvoice} className="gap-2 rounded-huge border-calm-border font-light text-calm-muted">
                    <Icon name="save" className="text-[18px]" /> Guardar Borrador
                  </Button>
                  <Button onClick={() => handleSaveInvoice(true)} disabled={savingInvoice} className="gap-2 rounded-huge font-light">
                    <Icon name="check" className="text-[18px]" /> Emitir Factura
                  </Button>
                  <Button variant="outline" onClick={() => setMostrarPreview(true)} disabled={savingInvoice} className="gap-2 rounded-huge border-calm-border font-light text-calm-muted">
                    <Icon name="visibility" className="text-[18px]" /> Previsualizar
                  </Button>
                  {editingInvoiceId && (
                    <button onClick={() => { resetForm(); setActiveTab('facturas'); }} className="ml-auto text-sm font-light text-calm-muted hover:text-calm-text transition-colors">Cancelar edición</button>
                  )}
                </div>
              </div>
            )}

            {/* ═══════════════════════════════════════
                TAB 3 — SERIES
               ═══════════════════════════════════════ */}
            {activeTab === 'series' && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="section-title">Series de Facturación</h2>
                    <p className="section-subtitle">Configura las series numéricas para tus facturas</p>
                  </div>
                  <Button onClick={openNewSeries} className="gap-2 rounded-huge font-light">
                    <Icon name="add" className="text-[18px]" /> Nueva Serie
                  </Button>
                </div>

                <div className="bg-white rounded-huge border border-calm-border overflow-hidden">
                  {series.length === 0 ? (
                    <div className="text-center py-16">
                      <Icon name="settings" className="text-[40px] text-calm-accent mx-auto block mb-3" />
                      <p className="text-calm-text font-light">No hay series configuradas</p>
                      <p className="text-sm text-calm-muted font-light mt-1">Crea tu primera serie para empezar a facturar</p>
                      <Button onClick={openNewSeries} className="mt-4 gap-2 rounded-huge font-light">
                        <Icon name="add" className="text-[18px]" /> Crear serie
                      </Button>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/60 hover:bg-slate-50/60 border-b border-calm-border">
                          <TableHead className="text-[11px] font-medium text-calm-muted uppercase tracking-widest">Código</TableHead>
                          <TableHead className="text-[11px] font-medium text-calm-muted uppercase tracking-widest">Nombre</TableHead>
                          <TableHead className="text-[11px] font-medium text-calm-muted uppercase tracking-widest">Tipo</TableHead>
                          <TableHead className="text-[11px] font-medium text-calm-muted uppercase tracking-widest">Prefijo</TableHead>
                          <TableHead className="text-center text-[11px] font-medium text-calm-muted uppercase tracking-widest">Siguiente Nº</TableHead>
                          <TableHead className="text-center text-[11px] font-medium text-calm-muted uppercase tracking-widest">VeriFactu</TableHead>
                          <TableHead className="text-right text-[11px] font-medium text-calm-muted uppercase tracking-widest">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {series.map(serie => (
                          <TableRow key={serie.id} className="hover:bg-slate-50/50 transition-colors">
                            <TableCell className="font-mono text-sm font-medium text-calm-text">{serie.codigo}</TableCell>
                            <TableCell className="text-calm-text font-light">{serie.nombre}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={cn(
                                'rounded-lg font-light',
                                serie.tipo === 'ordinaria' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                serie.tipo === 'rectificativa' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                'bg-slate-50 text-calm-muted border-calm-border'
                              )}>
                                {serie.tipo.charAt(0).toUpperCase() + serie.tipo.slice(1)}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-sm text-calm-muted">{serie.prefijo}</TableCell>
                            <TableCell className="text-center font-mono text-sm font-light text-calm-text">{serie.siguienteNumero}</TableCell>
                            <TableCell className="text-center">
                              {serie.verifactu ? <Icon name="check_circle" className="text-[16px] text-emerald-500" /> : <span className="text-calm-accent">—</span>}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="inline-flex items-center gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openEditSeries(serie)} title="Editar" className="h-8 w-8 rounded-2xl text-calm-muted hover:text-calm-text">
                                  <Icon name="edit" className="text-[18px]" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDeleteSeries(serie.id)} title="Eliminar" className="h-8 w-8 rounded-2xl text-calm-accent hover:text-red-600">
                                  <Icon name="delete" className="text-[18px]" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            )}

            {/* ═══════════════════════════════════════
                TAB 4 — TEMPLATES
               ═══════════════════════════════════════ */}
            {activeTab === 'templates' && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="section-title">Plantillas de Factura</h2>
                    <p className="section-subtitle">Personaliza el aspecto visual de tus facturas</p>
                  </div>
                  <Button onClick={openNewTemplate} className="gap-2 rounded-huge font-light">
                    <Icon name="add" className="text-[18px]" /> Nueva Plantilla
                  </Button>
                </div>

                {templates.length === 0 ? (
                  <div className="bg-white rounded-huge border border-calm-border text-center py-16">
                    <Icon name="palette" className="text-[40px] text-calm-accent mx-auto block mb-3" />
                    <p className="text-calm-text font-light">No hay plantillas configuradas</p>
                    <p className="text-sm text-calm-muted font-light mt-1">Crea una plantilla para personalizar tus facturas</p>
                    <Button onClick={openNewTemplate} className="mt-4 gap-2 rounded-huge font-light">
                      <Icon name="add" className="text-[18px]" /> Crear plantilla
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map(tmpl => (
                      <Card key={tmpl.id} className="overflow-hidden group bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge shadow-none hover:shadow-sm transition-all">
                        <div className="h-36 relative" style={{ background: `linear-gradient(135deg, ${tmpl.colorPrimario}, ${tmpl.colorSecundario})` }}>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-3 text-center shadow-sm">
                              <div className="w-8 h-2 rounded-full mx-auto mb-2" style={{ backgroundColor: tmpl.colorPrimario }} />
                              <div className="space-y-1">
                                <div className="w-16 h-1.5 bg-slate-200 rounded-full mx-auto" />
                                <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto" />
                                <div className="w-24 h-1.5 bg-slate-100 rounded-full mx-auto" />
                              </div>
                              <div className="mt-2 w-12 h-1.5 bg-slate-200 rounded-full mx-auto" />
                            </div>
                          </div>
                          {tmpl.activa && (
                            <Badge className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-emerald-700 gap-1 rounded-lg font-light">
                              <Icon name="check" className="text-[12px]" /> Activa
                            </Badge>
                          )}
                        </div>
                        <CardContent className="p-5">
                          <h3 className="font-medium text-calm-text text-sm">{tmpl.nombre}</h3>
                          <div className="flex items-center gap-2 mt-2">
                            <div className="w-4 h-4 rounded-full border border-calm-border" style={{ backgroundColor: tmpl.colorPrimario }} />
                            <div className="w-4 h-4 rounded-full border border-calm-border" style={{ backgroundColor: tmpl.colorSecundario }} />
                            <span className="text-xs text-calm-accent ml-1">{tmpl.colorPrimario}</span>
                          </div>
                          {tmpl.datosBancarios && (
                            <p className="text-xs text-calm-accent mt-2 truncate">
                              <Icon name="credit_card" className="text-[11px] mr-1 align-middle" />{tmpl.datosBancarios}
                            </p>
                          )}
                          <Separator className="my-3 bg-calm-border" />
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditTemplate(tmpl)} className="flex-1 gap-1.5 text-xs rounded-2xl font-light text-calm-muted hover:text-calm-text">
                              <Icon name="edit" className="text-[14px]" /> Editar
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(tmpl.id)} className="flex-1 gap-1.5 text-xs rounded-2xl font-light text-calm-muted hover:text-red-600">
                              <Icon name="delete" className="text-[14px]" /> Eliminar
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                    {/* Add template card */}
                    <button
                      onClick={openNewTemplate}
                      className="flex flex-col items-center justify-center min-h-[240px] border-2 border-dashed border-calm-border rounded-huge text-calm-accent hover:text-calm-text hover:border-calm-text/30 hover:bg-slate-50 transition-all group"
                    >
                      <Icon name="add" className="text-[28px] mb-2 transition-transform group-hover:scale-110" />
                      <span className="text-sm font-light">Añadir plantilla</span>
                      <div className="w-24 h-1.5 bg-steel-azure-50 rounded-full mx-auto" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          MODALS
         ═══════════════════════════════════════ */}

      {/* Series Modal */}
      <Dialog open={showSeriesModal} onOpenChange={(open) => { if (!open) { setShowSeriesModal(false); setEditingSeries(null); } }}>
        <DialogContent className="sm:max-w-md rounded-huge border-calm-border">
          <DialogHeader>
            <DialogTitle className="font-display font-light text-calm-text">{editingSeries ? 'Editar Serie' : 'Nueva Serie'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Código *</Label>
                <Input type="text" value={seriesForm.codigo} onChange={e => setSeriesForm(f => ({ ...f, codigo: e.target.value.toUpperCase() }))} placeholder="F" className="font-mono border-calm-border rounded-2xl text-sm font-light" />
              </div>
              <div>
                <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Nombre *</Label>
                <Input type="text" value={seriesForm.nombre} onChange={e => setSeriesForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Facturas Ordinarias" className="border-calm-border rounded-2xl text-sm font-light" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Tipo</Label>
              <Select value={seriesForm.tipo} onValueChange={v => setSeriesForm(f => ({ ...f, tipo: v as InvoiceSeries['tipo'] }))}>
                <SelectTrigger className="rounded-2xl border-calm-border text-sm font-light"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-2xl border-calm-border">
                  <SelectItem value="ordinaria" className="text-sm font-light">Ordinaria</SelectItem>
                  <SelectItem value="rectificativa" className="text-sm font-light">Rectificativa</SelectItem>
                  <SelectItem value="simplificada" className="text-sm font-light">Simplificada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Prefijo</Label>
                <Input type="text" value={seriesForm.prefijo} onChange={e => setSeriesForm(f => ({ ...f, prefijo: e.target.value }))} placeholder="F2026-" className="font-mono border-calm-border rounded-2xl text-sm font-light" />
              </div>
              <div>
                <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Siguiente Nº</Label>
                <Input type="number" value={seriesForm.siguienteNumero} onChange={e => setSeriesForm(f => ({ ...f, siguienteNumero: parseInt(e.target.value) || 1 }))} min="1" className="tabular-nums border-calm-border rounded-2xl text-sm font-light" />
              </div>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Checkbox id="series-verifactu" checked={seriesForm.verifactu} onCheckedChange={checked => setSeriesForm(f => ({ ...f, verifactu: !!checked }))} />
              <Label htmlFor="series-verifactu" className="text-sm text-calm-muted font-light cursor-pointer normal-case tracking-normal">
                Enviar a VeriFactu (obligatorio para facturas nacionales)
              </Label>
            </div>
            <div className="mt-2 p-4 bg-slate-50 rounded-2xl border border-calm-border">
              <span className="text-[10px] text-calm-muted uppercase tracking-[0.15em] block mb-1">Vista previa del número:</span>
              <span className="text-lg font-light font-mono text-calm-text">
                {seriesForm.prefijo}{String(seriesForm.siguienteNumero).padStart(4, '0')}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSeriesModal(false); setEditingSeries(null); }} className="rounded-huge border-calm-border font-light text-calm-muted">Cancelar</Button>
            <Button onClick={handleSaveSeries} className="rounded-huge font-light">{editingSeries ? 'Actualizar' : 'Crear Serie'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Modal */}
      <Dialog open={showTemplateModal} onOpenChange={(open) => { if (!open) { setShowTemplateModal(false); setEditingTemplate(null); } }}>
        <DialogContent className="sm:max-w-lg rounded-huge border-calm-border">
          <DialogHeader>
            <DialogTitle className="font-display font-light text-calm-text">{editingTemplate ? 'Editar Plantilla' : 'Nueva Plantilla'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Nombre de la plantilla *</Label>
              <Input type="text" value={templateForm.nombre} onChange={e => setTemplateForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Plantilla Corporativa" className="border-calm-border rounded-2xl text-sm font-light" />
            </div>
            <div>
              <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Logo</Label>
              <div className="flex items-center justify-center w-full h-24 border-2 border-dashed border-calm-border rounded-2xl bg-fresh-sky-50/30 hover:bg-fresh-sky-50 cursor-pointer transition">
                <div className="text-center">
                  <Icon name="upload" className="text-[20px] text-calm-accent mx-auto block mb-1" />
                  <span className="text-xs text-calm-muted font-light">Arrastra o haz clic para subir</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Color Primario</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={templateForm.colorPrimario} onChange={e => setTemplateForm(f => ({ ...f, colorPrimario: e.target.value }))} className="w-10 h-10 rounded-xl border border-calm-border cursor-pointer p-0.5" />
                  <Input type="text" value={templateForm.colorPrimario} onChange={e => setTemplateForm(f => ({ ...f, colorPrimario: e.target.value }))} className="flex-1 font-mono border-calm-border rounded-2xl text-sm font-light" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Color Secundario</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={templateForm.colorSecundario} onChange={e => setTemplateForm(f => ({ ...f, colorSecundario: e.target.value }))} className="w-10 h-10 rounded-xl border border-calm-border cursor-pointer p-0.5" />
                  <Input type="text" value={templateForm.colorSecundario} onChange={e => setTemplateForm(f => ({ ...f, colorSecundario: e.target.value }))} className="flex-1 font-mono border-calm-border rounded-2xl text-sm font-light" />
                </div>
              </div>
            </div>
            <div className="h-8 rounded-2xl" style={{ background: `linear-gradient(135deg, ${templateForm.colorPrimario}, ${templateForm.colorSecundario})` }} />
            <div>
              <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Datos Bancarios</Label>
              <Input type="text" value={templateForm.datosBancarios} onChange={e => setTemplateForm(f => ({ ...f, datosBancarios: e.target.value }))} placeholder="ES12 3456 7890 1234 5678 9012" className="font-mono border-calm-border rounded-2xl text-sm font-light" />
            </div>
            <div>
              <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Condiciones de Pago</Label>
              <Input type="text" value={templateForm.condicionesPago} onChange={e => setTemplateForm(f => ({ ...f, condicionesPago: e.target.value }))} placeholder="Pago a 30 días desde fecha de factura" className="border-calm-border rounded-2xl text-sm font-light" />
            </div>
            <div>
              <Label className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2 block">Pie de Página</Label>
              <Textarea value={templateForm.piePagina} onChange={e => setTemplateForm(f => ({ ...f, piePagina: e.target.value }))} rows={2} placeholder="Inscrita en el Registro Mercantil de..." className="resize-none border-calm-border rounded-2xl text-sm font-light" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); }} className="rounded-huge border-calm-border font-light text-calm-muted">Cancelar</Button>
            <Button onClick={handleSaveTemplate} className="rounded-huge font-light">{editingTemplate ? 'Actualizar' : 'Crear Plantilla'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={(open) => { if (!open) { setShowPreview(false); setPreviewInvoice(null); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto rounded-huge border-calm-border print:max-w-none print:max-h-none print:border-0 print:shadow-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 font-display font-light text-calm-text">
              {previewInvoice ? (
                <>Factura {previewInvoice.numero} <EstadoBadge estado={previewInvoice.estado} /></>
              ) : 'Factura'}
            </DialogTitle>
          </DialogHeader>
          {previewInvoice && (
            <div className="py-4 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2">Cliente</h4>
                  <p className="font-medium text-calm-text">{previewInvoice.clienteNombre}</p>
                  <p className="text-sm text-calm-muted font-light">{previewInvoice.clienteNif}</p>
                  {previewInvoice.clienteDireccion && <p className="text-sm text-calm-accent font-light mt-1">{previewInvoice.clienteDireccion}</p>}
                  {previewInvoice.clienteEmail && <p className="text-sm text-calm-accent font-light">{previewInvoice.clienteEmail}</p>}
                </div>
                <div className="text-right">
                  <h4 className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2">Detalles</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-calm-muted font-light">Emisión:</span> <span className="text-calm-text font-light">{formatDate(previewInvoice.fecha)}</span></p>
                    {previewInvoice.fechaVencimiento && <p><span className="text-calm-muted font-light">Vencimiento:</span> <span className="text-calm-text font-light">{formatDate(previewInvoice.fechaVencimiento)}</span></p>}
                    {previewInvoice.fechaOperacion && <p><span className="text-calm-muted font-light">Operación:</span> <span className="text-calm-text font-light">{formatDate(previewInvoice.fechaOperacion)}</span></p>}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-calm-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/60 hover:bg-slate-50/60 border-b border-calm-border">
                      <TableHead className="text-[11px] font-medium text-calm-muted uppercase tracking-widest">Concepto</TableHead>
                      <TableHead className="text-center text-[11px] font-medium text-calm-muted uppercase tracking-widest">Cant.</TableHead>
                      <TableHead className="text-right text-[11px] font-medium text-calm-muted uppercase tracking-widest">Precio</TableHead>
                      <TableHead className="text-center text-[11px] font-medium text-calm-muted uppercase tracking-widest">IVA</TableHead>
                      <TableHead className="text-right text-[11px] font-medium text-calm-muted uppercase tracking-widest">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewInvoice.lineas.map((line, idx) => {
                      const sub = line.cantidad * line.precioUnitario;
                      const disc = sub * (line.descuento / 100);
                      const base = sub - disc;
                      const ivaR = line.tipoIva === -1 ? 0 : line.tipoIva;
                      const iva = base * (ivaR / 100);
                      const total = base + iva + base * (line.recargoEquivalencia / 100) - base * (line.retencion / 100);
                      return (
                        <TableRow key={idx} className="hover:bg-slate-50/50">
                          <TableCell className="text-calm-text font-light">
                            {line.concepto}
                            {line.esSuplido && <span className="ml-1 text-xs text-amber-600">(Suplido)</span>}
                          </TableCell>
                          <TableCell className="text-center text-calm-muted tabular-nums font-light">{formatNumber(line.cantidad, 2)}</TableCell>
                          <TableCell className="text-right text-calm-muted tabular-nums font-light">{formatCurrency(line.precioUnitario)}</TableCell>
                          <TableCell className="text-center text-calm-muted font-light">{line.tipoIva === -1 ? 'Exento' : `${line.tipoIva}%`}</TableCell>
                          <TableCell className="text-right font-medium text-calm-text tabular-nums">{formatCurrency(total)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end">
                <div className="w-72 space-y-1.5">
                  <div className="flex justify-between text-sm"><span className="text-calm-muted font-light">Base Imponible</span><span className="text-calm-text tabular-nums font-light">{formatCurrency(previewInvoice.baseImponible)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-calm-muted font-light">IVA</span><span className="text-calm-text tabular-nums font-light">{formatCurrency(previewInvoice.totalIva)}</span></div>
                  {previewInvoice.totalRecargoEquivalencia > 0 && <div className="flex justify-between text-sm"><span className="text-calm-muted font-light">R. Equivalencia</span><span className="text-calm-text tabular-nums font-light">{formatCurrency(previewInvoice.totalRecargoEquivalencia)}</span></div>}
                  {previewInvoice.totalRetenciones > 0 && <div className="flex justify-between text-sm"><span className="text-calm-muted font-light">Retenciones</span><span className="text-red-600 tabular-nums font-light">-{formatCurrency(previewInvoice.totalRetenciones)}</span></div>}
                  {previewInvoice.totalSuplidos > 0 && <div className="flex justify-between text-sm"><span className="text-calm-muted font-light">Suplidos</span><span className="text-calm-text tabular-nums font-light">{formatCurrency(previewInvoice.totalSuplidos)}</span></div>}
                  <div className="pt-2 mt-1 border-t-2 border-calm-text">
                    <div className="flex justify-between"><span className="font-medium text-calm-text text-sm">TOTAL</span><span className="text-xl font-light text-calm-text tabular-nums font-display">{formatCurrency(previewInvoice.totalFactura)}</span></div>
                  </div>
                </div>
              </div>

              {previewInvoice.notas && (
                <div className="p-4 bg-slate-50 rounded-2xl border border-calm-border">
                  <p className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-1">Notas</p>
                  <p className="text-sm text-calm-text font-light whitespace-pre-wrap">{previewInvoice.notas}</p>
                </div>
              )}
            </div>
          )}
          {previewInvoice && (
            <DialogFooter className="justify-between w-full">
              <div className="flex items-center gap-2">
                <VeriFactuIcon status={previewInvoice.verifactu} />
                <span className="text-xs text-calm-muted font-light">VeriFactu</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleSendEmail(previewInvoice.id)} className="gap-1.5 rounded-huge border-calm-border font-light text-calm-muted">
                  <Icon name="mail" className="text-[16px]" /> Enviar
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDownloadXml(previewInvoice.id)} className="gap-1.5 rounded-huge border-calm-border font-light text-calm-muted">
                  <Icon name="download" className="text-[16px]" /> XML
                </Button>
                <Button size="sm" onClick={() => { setShowPreview(false); setPreviewInvoice(null); }} className="rounded-huge font-light">Cerrar</Button>
              </div>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Hidden CSV file input for line import */}
      <input
        ref={csvFileInputRef}
        type="file"
        accept=".csv,.txt"
        onChange={handleCsvFileChange}
        className="hidden"
      />

      {/* Form Preview Dialog */}
      <Dialog open={mostrarPreview} onOpenChange={setMostrarPreview}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto rounded-huge border-calm-border print:max-w-none print:max-h-none print:border-0 print:shadow-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 font-display font-light text-calm-text">
              <Icon name="visibility" className="text-[20px] text-calm-accent" />
              Vista previa de factura
              <Badge variant="outline" className="ml-2 rounded-lg font-light text-calm-muted border-calm-border">Borrador</Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-6 print:py-0">
            {/* Emisor / Receptor header */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2">Emisor</h4>
                <p className="font-medium text-calm-text">{formEmisorNombre || 'Sin nombre'}</p>
                <p className="text-sm text-calm-muted font-light font-mono">{formEmisorCif || '—'}</p>
                {formEmisorDireccion && <p className="text-sm text-calm-accent font-light mt-1">{formEmisorDireccion}</p>}
                {(formEmisorCp || formEmisorCiudad) && (
                  <p className="text-sm text-calm-accent font-light">
                    {[formEmisorCp, formEmisorCiudad].filter(Boolean).join(' ')}
                  </p>
                )}
              </div>
              <div>
                <h4 className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-2">Receptor</h4>
                <p className="font-medium text-calm-text">{formClienteNombre || 'Sin nombre'}</p>
                <p className="text-sm text-calm-muted font-light font-mono">{formClienteNif || '—'}</p>
                {formClienteDireccion && <p className="text-sm text-calm-accent font-light mt-1">{formClienteDireccion}</p>}
                {(formClienteCp || formClienteCiudad) && (
                  <p className="text-sm text-calm-accent font-light">
                    {[formClienteCp, formClienteCiudad].filter(Boolean).join(' ')}
                  </p>
                )}
                {formClienteEmail && <p className="text-sm text-calm-accent font-light">{formClienteEmail}</p>}
              </div>
            </div>

            {/* Invoice meta */}
            <div className="flex flex-wrap items-center gap-6 p-4 bg-slate-50 rounded-2xl border border-calm-border">
              <div>
                <span className="text-[10px] text-calm-muted uppercase tracking-[0.15em] block">Nº Factura</span>
                <span className="text-lg font-light font-mono text-calm-text">{formNumeroPreview || '—'}</span>
              </div>
              <div>
                <span className="text-[10px] text-calm-muted uppercase tracking-[0.15em] block">Fecha Emisión</span>
                <span className="text-sm font-light text-calm-text">{formFechaEmision ? formatDate(formFechaEmision) : '—'}</span>
              </div>
              {formFechaVencimiento && (
                <div>
                  <span className="text-[10px] text-calm-muted uppercase tracking-[0.15em] block">Vencimiento</span>
                  <span className="text-sm font-light text-calm-text">{formatDate(formFechaVencimiento)}</span>
                </div>
              )}
              {formFechaOperacion && (
                <div>
                  <span className="text-[10px] text-calm-muted uppercase tracking-[0.15em] block">Operación</span>
                  <span className="text-sm font-light text-calm-text">{formatDate(formFechaOperacion)}</span>
                </div>
              )}
            </div>

            {/* Line items table */}
            <div className="rounded-2xl border border-calm-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/60 hover:bg-slate-50/60 border-b border-calm-border">
                    <TableHead className="text-[11px] font-medium text-calm-muted uppercase tracking-widest">Descripción</TableHead>
                    <TableHead className="text-center text-[11px] font-medium text-calm-muted uppercase tracking-widest">Cantidad</TableHead>
                    <TableHead className="text-right text-[11px] font-medium text-calm-muted uppercase tracking-widest">Precio</TableHead>
                    <TableHead className="text-center text-[11px] font-medium text-calm-muted uppercase tracking-widest">IVA%</TableHead>
                    <TableHead className="text-right text-[11px] font-medium text-calm-muted uppercase tracking-widest">Importe</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formLineas.map((line, idx) => {
                    const calc = lineCalculations[idx];
                    return (
                      <TableRow key={line.id} className="hover:bg-slate-50/50">
                        <TableCell className="text-calm-text font-light">
                          {line.concepto || <span className="text-calm-accent italic">Sin descripción</span>}
                          {line.esSuplido && <span className="ml-1 text-xs text-amber-600">(Suplido)</span>}
                          {line.descuento > 0 && <span className="ml-1 text-xs text-calm-muted">(-{formatNumber(line.descuento)}% dto.)</span>}
                        </TableCell>
                        <TableCell className="text-center text-calm-muted tabular-nums font-light">{formatNumber(line.cantidad, 2)}</TableCell>
                        <TableCell className="text-right text-calm-muted tabular-nums font-light">{formatCurrency(line.precioUnitario)}</TableCell>
                        <TableCell className="text-center text-calm-muted font-light">{line.tipoIva === -1 ? 'Exento' : `${line.tipoIva}%`}</TableCell>
                        <TableCell className="text-right font-medium text-calm-text tabular-nums">{formatCurrency(calc?.totalLinea || 0)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-80 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-calm-muted font-light">Base Imponible</span>
                  <span className="text-calm-text tabular-nums font-light">{formatCurrency(invoiceTotals.baseImponible)}</span>
                </div>
                {Object.entries(invoiceTotals.ivaBreakdown).map(([rate, data]) => (
                  <div key={rate} className="flex justify-between text-sm">
                    <span className="text-calm-muted font-light">IVA {rate}% <span className="text-xs text-calm-accent">s/ {formatCurrency(data.base)}</span></span>
                    <span className="text-calm-text tabular-nums font-light">{formatCurrency(data.cuota)}</span>
                  </div>
                ))}
                {invoiceTotals.totalRecargoEquivalencia > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-calm-muted font-light">Recargo Equivalencia</span>
                    <span className="text-calm-text tabular-nums font-light">{formatCurrency(invoiceTotals.totalRecargoEquivalencia)}</span>
                  </div>
                )}
                {invoiceTotals.totalRetenciones > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-calm-muted font-light">Retenciones</span>
                    <span className="text-red-600 tabular-nums font-light">-{formatCurrency(invoiceTotals.totalRetenciones)}</span>
                  </div>
                )}
                {invoiceTotals.totalSuplidos > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-calm-muted font-light">Suplidos</span>
                    <span className="text-calm-text tabular-nums font-light">{formatCurrency(invoiceTotals.totalSuplidos)}</span>
                  </div>
                )}
                <div className="pt-3 mt-2 border-t-2 border-calm-text">
                  <div className="flex justify-between items-baseline">
                    <span className="text-[10px] font-medium text-calm-text uppercase tracking-widest">Total a pagar</span>
                    <span className="text-2xl font-light text-calm-text tabular-nums font-display">{formatCurrency(invoiceTotals.totalFactura)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            {formNotas && (
              <div className="p-4 bg-slate-50 rounded-2xl border border-calm-border">
                <p className="text-[10px] text-calm-muted uppercase tracking-[0.15em] mb-1">Observaciones</p>
                <p className="text-sm text-calm-text font-light whitespace-pre-wrap">{formNotas}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMostrarPreview(false)} className="rounded-huge border-calm-border font-light text-calm-muted">
              Cerrar
            </Button>
            <Button onClick={() => { setMostrarPreview(false); handleSaveInvoice(false); }} className="gap-2 rounded-huge font-light">
              <Icon name="save" className="text-[16px]" /> Guardar Borrador
            </Button>
            <Button onClick={() => { setMostrarPreview(false); handleSaveInvoice(true); }} className="gap-2 rounded-huge font-light bg-emerald-600 hover:bg-emerald-700">
              <Icon name="check" className="text-[16px]" /> Emitir Factura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
